<script lang="ts">
import type { ButtonProps } from '#layers/ui/components/Button.vue';
import type { IconFrameProps } from '#layers/ui/components/IconFrame.vue';
import DButton from '#layers/ui/components/Button.vue';
import DColorPicker from '#layers/ui/components/ColorPicker.vue';
import DColorSwatches from '#layers/ui/components/ColorSwatches.vue';
import DIconFrame from '#layers/ui/components/IconFrame.vue';
import DInput from '#layers/ui/components/Input.vue';
import { DEFAULT_COLORS } from '#layers/ui/utils/colors';

export interface IconPickerProps {
	/** Show color picker section */
	pickColor?: boolean;
	/** Placeholder icon when no value set */
	placeholderIcon?: string;
	/** Default color (used when pickColor but no color selected) */
	defaultColor?: string;
	/** Full-width mode showing icon name + clear button */
	block?: boolean;
	/** Size of the trigger button */
	size?: ButtonProps['size'];
	variant?: IconFrameProps['variant'];
	spacing?: IconFrameProps['spacing'];
	/** Custom color preset swatches */
	colorPresets?: string[];
	clear?: boolean;
	readonly?: boolean;
	disabled?: boolean;
}
</script>

<script lang="ts" setup>
import { buildIcon, getIcon } from '@iconify/vue';
import { until } from '@vueuse/core';
import { computed, h, ref, useId, useTemplateRef, watch } from 'vue';
import { RecycleScroller } from 'vue-virtual-scroller';
import { useAppConfig } from '#app/config';
import { useFormField } from '#layers/ui/composables/use-form-field';
import { useGridNavigation } from '#layers/ui/composables/use-grid-navigation';
import { useIconSearch } from '#layers/ui/composables/use-icon-search';

const props = withDefaults(defineProps<IconPickerProps>(), {
	colorPresets: () => [...DEFAULT_COLORS],
	variant: 'link',
	spacing: 'comfortable',
	clear: true,
});

const iconDataCache = new Map<string, { body: string; attrs: Record<string, string | number> }>();

function getCachedIconData(name: string) {
	const cached = iconDataCache.get(name);
	if (cached) return cached;

	const raw = getIcon(name);
	if (!raw) return null;

	const { body, attributes } = buildIcon(raw);
	const data = { body, attrs: { ...attributes, 'class': 'size-full', 'aria-hidden': 'true', 'focusable': 'false' } };
	iconDataCache.set(name, data);
	return data;
}

function RawIcon(iconProps: { name: string }) {
	const data = getCachedIconData(iconProps.name);
	if (!data) return null;
	return h('svg', { ...data.attrs, innerHTML: data.body });
}

const icon = defineModel<string | null>();
const color = defineModel<string | null>('color');

const appConfig = useAppConfig();
const openPicker = ref(false);
const showFullColorPicker = ref(false);
const { size: formGroupSize } = useFormField<IconPickerProps>(props);

const effectiveSize = computed(() => props.size ?? formGroupSize.value ?? 'md');

const triggerSizeClass = computed(() => {
	const sizes: Record<string, string> = { xs: 'size-8', sm: 'size-8', md: 'size-10', lg: 'size-12', xl: 'size-13' };
	return sizes[effectiveSize.value] ?? 'size-8';
});

const { search, displayedIcons } = useIconSearch();

const gridRef = useTemplateRef<HTMLElement>('gridRef');
const scrollerRef = useTemplateRef<{ scrollToItem: (index: number, options?: { align?: string }) => void }>('scroller');
const searchInputRef = useTemplateRef('searchInput');

const ICON_SIZE = 28;
const columns = ref(14);

/** Chunk displayed icons into fixed-size rows so RecycleScroller can virtualize them. */
const rows = computed(() => {
	const rowSize = columns.value;
	const out: Array<{ id: number; icons: typeof displayedIcons.value }> = [];

	for (let i = 0; i < displayedIcons.value.length; i += rowSize) {
		out.push({ id: i / rowSize, icons: displayedIcons.value.slice(i, i + rowSize) });
	}

	return out;
});

function getGlobalIndex(rowIndex: number, colIndex: number) {
	return rowIndex * columns.value + colIndex;
}

const total = computed(() => displayedIcons.value.length);

const {
	focusedIndex,
	setFocused,
	handleArrowDown,
	handleArrowLeft,
	handleArrowRight,
	handleArrowUp,
	handleActivate,
} = useGridNavigation({
	columns,
	total,
	onActivate: () => {
		const entry = displayedIcons.value[focusedIndex.value];

		if (entry) selectIcon(entry.name, focusedIndex.value);
	},
});

watch(search, () => {
	setFocused(-1);
});

function scrollToRow(rowIndex: number, align: 'nearest' | 'start' | 'center' | 'end' = 'nearest') {
	const scroller = scrollerRef.value;

	if (scroller && typeof scroller.scrollToItem === 'function') {
		scroller.scrollToItem(rowIndex, { align });
	}
}

watch(focusedIndex, (index) => {
	if (index < 0) return;
	scrollToRow(Math.floor(index / columns.value));
});

function focusSearchInput() {
	setFocused(-1);
	const el = (searchInputRef.value as any)?.$el;
	if (el) (el.querySelector('input') ?? el).focus();
}

function handleSearchKeydown(event: KeyboardEvent) {
	if (event.key === 'ArrowDown' && displayedIcons.value.length > 0) {
		event.preventDefault();
		setFocused(0);
		gridRef.value?.focus();
	}
}

function handleGridKeydown(event: KeyboardEvent) {
	if (displayedIcons.value.length === 0) return;

	switch (event.key) {
		case 'ArrowRight':
			event.preventDefault();
			handleArrowRight(event);
			return;
		case 'ArrowLeft':
			event.preventDefault();
			handleArrowLeft(event);
			return;
		case 'ArrowDown':
			event.preventDefault();
			handleArrowDown(event);
			return;
		case 'ArrowUp':
			event.preventDefault();

			if (focusedIndex.value < columns.value) {
				focusSearchInput();
				return;
			}

			handleArrowUp(event);
			return;
		case 'Enter':
			event.preventDefault();
			handleActivate(event);
			return;
		case 'Escape':
			event.preventDefault();
			focusSearchInput();
			return;
		default:
			if (event.key.length === 1 && !event.ctrlKey && !event.metaKey) {
				focusSearchInput();
			}
	}
}

function selectIcon(iconName: string, index: number) {
	icon.value = iconName;
	setFocused(index);

	if (!props.pickColor) {
		openPicker.value = false;
	}
}

function clearIcon() {
	icon.value = null;
	color.value = null;
	setFocused(-1);
}

watch(openPicker, async (val) => {
	if (!val) return;

	search.value = '';
	showFullColorPicker.value = false;
	setFocused(-1);

	if (!icon.value) return;

	// Popover content (and the scroller) mounts asynchronously, so wait for the
	// scroller ref to populate rather than guessing with nextTick.
	await until(scrollerRef).toBeTruthy();
	if (!openPicker.value) return;

	const selectedIndex = displayedIcons.value.findIndex((entry) => entry.name === icon.value);
	if (selectedIndex === -1) return;

	scrollToRow(Math.floor(selectedIndex / columns.value), 'start');
});

const displayedIcon = computed(() => icon.value || props.placeholderIcon);
const effectiveColor = computed(() => color.value || props.defaultColor || 'var(--ui-text)');

const listboxId = useId();

const focusedIconId = computed(() => {
	const entry = displayedIcons.value[focusedIndex.value];
	return entry ? `${listboxId}-${entry.name}` : undefined;
});
</script>

<template>
	<DPopover
		v-model:open="openPicker"
		:content="{ align: 'start' }"
		v-bind="$attrs"
	>
		<slot>
			<DButton
				v-if="block"
				color="neutral"
				variant="outline"
				:trailing-icon="!readonly ? 'lucide:chevron-down' : undefined"
				block
				class="items-center overflow-hidden font-normal"
				:class="{ 'pointer-events-none': readonly }"
				:size="effectiveSize"
				:aria-label="$t('icon_picker.select_icon')"
				:disabled
				:readonly
			>
				<DIconFrame
					v-if="displayedIcon"
					:name="displayedIcon"
					:color="effectiveColor"
					variant="link"
					spacing="tight"
					class="size-4"
					:disabled
				/>
				<div v-else class="size-4" />

				<div class="mr-auto flex-1 truncate">
					{{ icon }}
				</div>
				<DButton
					v-if="icon && !readonly"
					icon="lucide:x"
					variant="ghost"
					color="neutral"
					:size="effectiveSize"
					:ui="{ base: '-my-1 opacity-50 hover:bg-accented hover:opacity-100' }"
					:aria-label="$t('icon_picker.remove_icon')"
					:disabled
					@click.stop.prevent="clearIcon"
				/>
			</DButton>

			<DButton
				v-else
				color="neutral"
				variant="outline"
				class="
					inline-flex aspect-square w-min! cursor-pointer items-center justify-center overflow-clip p-0! font-normal
					ring-default
				"
				:class="[triggerSizeClass]"
				:aria-label="$t('icon_picker.select_icon')"
				:readonly
				:disabled
			>
				<DIconFrame
					v-if="displayedIcon"
					:name="displayedIcon"
					:color="effectiveColor"
					:variant
					:spacing
					:disabled
					class="aspect-square h-full"
				/>
				<DIcon v-else name="lucide:plus" class="size-5 text-dimmed" />
			</DButton>
		</slot>

		<template #content>
			<div
				class="flex max-h-(--reka-popper-available-height) w-105 max-w-[calc(100vw-2rem)] flex-col divide-y divide-default"
			>
				<DInput
					ref="searchInput"
					v-model="search"
					:placeholder="$t('icon_picker.search_placeholder')"
					variant="none"
					class="w-full"
					size="md"
					autofocus
					:ui="{ base: 'h-11 px-4' }"
					:readonly
					:disabled
					@keydown="handleSearchKeydown"
				/>

				<div
					ref="gridRef"
					role="listbox"
					:aria-activedescendant="focusedIconId"
					:aria-label="$t('icon_picker.select_icon')"
					tabindex="-1"
					class="flex max-h-70 flex-1 flex-col overflow-hidden outline-none"
					:style="{ '--icon-color': effectiveColor }"
					@keydown="handleGridKeydown"
				>
					<div
						v-if="rows.length === 0"
						class="flex h-full items-center justify-center text-sm text-muted"
					>
						{{ $t('icon_picker.no_results') }}
					</div>

					<RecycleScroller
						v-else
						ref="scroller"
						:items="rows"
						:item-size="ICON_SIZE"
						key-field="id"
						direction="vertical"
						class="flex-1 scrollbar-thin scrollbar-neutral overflow-x-hidden overflow-y-auto overscroll-none p-1 px-3"
					>
						<template #default="{ item: row, index: rowIndex }">
							<div class="flex">
								<div
									v-for="(entry, colIndex) in row.icons"
									:id="`${listboxId}-${entry.name}`"
									:key="entry.name"
									role="option"
									class="
										inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-sm p-1.5 text-(--icon-color)
										hover:bg-(--icon-color)/10
										aria-selected:bg-(--icon-color)/10
									"
									:class="{ 'bg-(--icon-color)/10': getGlobalIndex(rowIndex, colIndex) === focusedIndex }"
									:aria-label="entry.label"
									:aria-selected="entry.name === icon || undefined"
									@click="selectIcon(entry.name, getGlobalIndex(rowIndex, colIndex))"
								>
									<RawIcon :name="entry.name" />
								</div>
							</div>
						</template>
					</RecycleScroller>
				</div>
				<div v-if="pickColor" class="p-3 px-4" data-test-id="color-swatches">
					<DColorSwatches
						v-if="!showFullColorPicker"
						v-model="color"
						:presets="colorPresets"
					>
						<template #trailing>
							<button
								class="
									ms-auto flex size-6 shrink-0 items-center justify-center rounded-full
									bg-[conic-gradient(var(--stop-0),var(--stop-1),var(--stop-2),var(--stop-3),var(--stop-4),var(--stop-5),var(--stop-0))]
									transition-all
									hover:scale-110
									focus:outline-none
									focus-visible:ring-1 focus-visible:ring-primary
								"
								:aria-label="$t('icon_picker.show_color_picker')"
								:style="Object.fromEntries(DEFAULT_COLORS.map((color, idx) => [`--stop-${idx}`, color]))"
								@click="showFullColorPicker = true"
							/>
						</template>
					</DColorSwatches>

					<div v-else class="flex flex-col gap-3">
						<div class="flex items-center justify-between">
							<button
								class="flex items-center gap-1 text-xs text-muted hover:text-highlighted"
								@click="showFullColorPicker = false"
							>
								<DIcon :name="appConfig.ui.icons.back" class="size-3" />
								{{ $t('icon_picker.back_to_swatches') }}
							</button>
						</div>
						<DColorPicker
							:model-value="color ?? undefined"
							:default-value="defaultColor ?? colorPresets[0]"
							orientation="horizontal"
							:ui="{ selector: 'w-full', track: 'flex-1' }"
							@update:model-value="color = $event"
						/>
					</div>
				</div>
				<div v-if="clear && !readonly && (icon || color)" class="flex justify-end p-2">
					<DButton variant="ghost" color="neutral" class="font-normal" @click="clearIcon">
						{{ $t('icon_picker.remove_icon') }}
					</DButton>
				</div>
			</div>
		</template>
	</DPopover>
</template>
